import type { ElementType } from "react";
import { useState } from "react";
import { Form, Formik, useFormikContext, type FormikHelpers } from "formik";
import { object } from "yup";
// @ts-expect-error pure js
import { sanitizeValues, stringMax } from "../../models/utils";
import {
  EMAIL_INVALID_MESSAGE,
  EMAIL_REGEX,
} from "../../utils/constants/validation";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
import Avatar from "../UI/Avatar";
import Button from "../UI/Button";
import Callout from "../UI/Callout";
import { ModalDialogFooter } from "../UI/ModalDialog";
import Card from "../UI/Card";
import Text from "../UI/Text";
import FormikSubmitButton from "../UI/FormikSubmitButton";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import { getDisplayName, getInitials } from "./profileUtils";
import type { Profile } from "./types";
import styles from "./RepresentantForm.module.css";

interface RepresentantFormValues {
  prenom: string;
  nom: string;
  mail: string;
  tel: string;
}

const representantSchema = object({
  prenom: stringMax(250).required("Le prénom est obligatoire"),
  nom: stringMax(250).required("Le nom est obligatoire"),
  mail: stringMax(250)
    .matches(EMAIL_REGEX, EMAIL_INVALID_MESSAGE)
    .required("L'adresse e-mail est obligatoire"),
  tel: stringMax(20).notRequired(),
});

function RepresentantFormHeader() {
  const { values } = useFormikContext<RepresentantFormValues>();
  const initials = getInitials(values as Profile);
  const displayName = getDisplayName(values as Profile);

  return (
    <div className={styles.header}>
      <Avatar initials={initials || "?"} size="md" variant="purple" />
      <div>
        <Text weight="bold">{displayName || "Nouveau représentant légal"}</Text>
        <Text variant="deci" color="muted">
          Représentant légal — signe le bail à la place de la personne
          représentée
        </Text>
      </div>
    </div>
  );
}

interface RepresentantFormProps {
  profile?: Profile;
  inline?: boolean;
  modal?: ElementType;
  onSubmit: (data: Profile) => Promise<void> | void;
  onCancel?: () => void;
}

export default function RepresentantForm({
  profile,
  inline = false,
  modal: ModalElement,
  onSubmit,
  onCancel,
}: RepresentantFormProps) {
  const [error, setError] = useState("");

  const initialValues: RepresentantFormValues = {
    prenom: profile?.prenom ?? "",
    nom: profile?.nom ?? "",
    mail: profile?.mail ?? "",
    tel: profile?.tel ?? "",
  };

  async function submitHandler(
    values: RepresentantFormValues,
    { resetForm }: FormikHelpers<RepresentantFormValues>,
  ) {
    // On conserve les champs techniques (tmp_id, tmp_legal_representative_of) du
    // profil en édition, et on force type=1 : un représentant est une personne physique.
    const data = {
      ...profile,
      ...sanitizeValues(values),
      type: 1,
    } as Profile;
    try {
      await onSubmit(data);
      resetForm();
      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    }
  }

  const fields = (
    <div className={styles.fields}>
      <div className={styles.nameRow}>
        <TextInput
          type="text"
          name="prenom"
          label="Prénom"
          placeholder="Jean"
          mandatory
          marginForm=""
        />
        <TextInput
          type="text"
          name="nom"
          label="Nom"
          placeholder="Dupont"
          mandatory
          marginForm=""
        />
      </div>
      <TextInput
        type="email"
        name="mail"
        label="Adresse e-mail"
        placeholder="adresseemail@qalimo.fr"
        mandatory
        marginForm=""
      />
      <TextInput
        type="text"
        name="tel"
        label="Téléphone"
        placeholder="0622244432"
        marginForm=""
      />
      <Callout variant="danger">{error}</Callout>
    </div>
  );

  return (
    <Formik<RepresentantFormValues>
      initialValues={initialValues}
      enableReinitialize
      onSubmit={submitHandler}
      validationSchema={representantSchema}
    >
      {(formikProps) => {
        const { submitForm, isSubmitting, resetForm, dirty } = formikProps;
        const buttons = (
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                resetForm();
                if (onCancel) onCancel();
              }}
            >
              Annuler
            </Button>
            {inline ? (
              <Button
                type="button"
                onClick={() => submitForm()}
                isLoading={isSubmitting}
              >
                Enregistrer
              </Button>
            ) : (
              <FormikSubmitButton>Enregistrer</FormikSubmitButton>
            )}
          </>
        );
        const actions = <div className={styles.actions}>{buttons}</div>;

        if (inline) {
          // Inline: pas de <form> imbriqué (le parent est déjà dans un Formik).
          return (
            <Card padding="xs" className={styles.card}>
              <RepresentantFormHeader />
              {fields}
              {actions}
            </Card>
          );
        }

        if (!ModalElement) {
          return (
            <Form>
              <RepresentantFormHeader />
              {fields}
              {actions}
            </Form>
          );
        }

        return (
          <ModalElement
            stickyHeader
            confirmCloseText={
              "Votre formulaire n'a pas été sauvegardé. Voulez-vous vraiment quitter ?"
            }
            shouldConfirm={() => dirty}
            size={"medium"}
            blueHeader
            header={<RepresentantFormHeader />}
          >
            <Form>
              {fields}
              <ModalDialogFooter>{buttons}</ModalDialogFooter>
            </Form>
          </ModalElement>
        );
      }}
    </Formik>
  );
}
